import { useContext } from 'react';

import { Card, Col, Text } from '@gympass/yoga';
import { TextField } from '@mui/material';
import { DateField } from '@mui/x-date-pickers-pro';
import { useFlag } from '@unleash/proxy-client-react';
import { navigateToEditPhone } from '@wellz/utils';
import withAuth from 'hocs/withAuth/withAuth';
import Instagram from 'icons/instagram.svg';
import { FormattedMessage, useIntl } from 'react-intl';
import { FeatureToggle } from 'utils/unleash/toggles';

import { SpecialistContext, useModals, usePermissionConfig } from 'context';
import useOnInit from 'hooks/useOnInit';
import useRequest from 'hooks/useRequest';
import { getAwsExports } from 'services/amplify';
import { getSpecialistProfessionalInfo } from 'services/specialist';

import CareProviderAtualizationInfo from 'components/CareProviderAtualizationInfo';
import { UpdatePhoneNumberBanner } from 'components/index';
import Loading from 'components/Loading';
import { PublishedProfileComponentProps } from 'modals/PublishedProfile/types';

import * as S from './styles';

const CareProviderInfo: React.FC = () => {
  const intl = useIntl();
  const { openModal } = useModals();
  const { hasAccess } = usePermissionConfig();
  const newLoginFlowEnabled = useFlag(FeatureToggle.NewLoginFlow);

  const {
    specialistInfo,
    specialistProfessionalInfo,
    setSpecialistProfessionalInfo,
  } = useContext(SpecialistContext);

  const getSpecialistProfessionalInfoRequest = useRequest();

  const loadSpecialistProfessionalInfo = async () => {
    getSpecialistProfessionalInfoRequest.setLoading();
    try {
      const response = await getSpecialistProfessionalInfo();
      setSpecialistProfessionalInfo(response);
      getSpecialistProfessionalInfoRequest.setSuccess();
    } catch (e) {
      getSpecialistProfessionalInfoRequest.setError(e);
    }
  };

  const handleSocialNavigation = () => {
    return window.open(specialistInfo?.socialLink, '_blank');
  };

  const handlePublishedProfileModal = () => {
    const avatar = specialistInfo?.avatar;

    const specialistAvatar = avatar && avatar[0] ? avatar || '' : '';

    openModal<PublishedProfileComponentProps>('publishedProfile', {
      props: {
        specialistInfo: {
          avatarURL: specialistAvatar,
          fullName: specialistInfo?.fullName || '',
          occupation: specialistProfessionalInfo?.type || '',
          professionalNumber:
            specialistProfessionalInfo?.professionalNumber || '',
          socialLink: specialistInfo?.socialLink,
          socialUsername: specialistInfo?.socialUsername,
          videoId: specialistInfo?.videoId,
          about: specialistProfessionalInfo?.aboutYou || '',
          specialities: specialistProfessionalInfo?.specialities || [],
          education: specialistProfessionalInfo?.education || '',
        },
      },
    });
  };

  const handlePhoneNumberEdit = async () => {
    const config = await getAwsExports();

    navigateToEditPhone({
      clientId: config.aws_user_pools_web_client_id,
    });
  };

  const PersonalInfo = () => {
    if (!hasAccess('profile.personal')) return <></>;
    return (
      <S.StyledCard>
        <S.CardHeader>
          <Text.H5 data-testid="personalCardTitle">
            <FormattedMessage id="CARE_PROVIDER.PAGE.PERSONAL.CARD.TITLE" />
          </Text.H5>
        </S.CardHeader>
        <Card.Content>
          <S.PersonalInfoContainer>
            <S.VisualInfoContainer>
              {specialistInfo?.avatar && specialistInfo.avatar ? (
                <S.CareProviderAvatar
                  data-testid="specialistAvatar"
                  src={specialistInfo?.avatar}
                  alt="Specialist avatar"
                  hasVideo={!!specialistInfo.videoId}
                  onClick={handlePublishedProfileModal}
                />
              ) : (
                <S.CareProviderAvatar
                  onClick={handlePublishedProfileModal}
                  data-testid="specialistAvatarPlaceholder"
                  hasVideo={!!specialistInfo && !!specialistInfo.videoId}
                >
                  {specialistInfo?.fullName && (
                    <S.CareProviderAvatarText>
                      {specialistInfo.fullName
                        .split(' ')
                        .slice(0, 2)
                        .map(word => word[0])
                        .join(',')
                        .replace(',', '')}
                    </S.CareProviderAvatarText>
                  )}
                </S.CareProviderAvatar>
              )}
              <S.HasVideoSpan
                hasVideo={!!specialistInfo && !!specialistInfo.videoId}
                data-testid="hasVideoSpan"
              >
                {!!specialistInfo && !!specialistInfo.videoId ? (
                  <FormattedMessage id="CARE_PROVIDER.PAGE.HEADER.PROFILE.HAS_VIDEO" />
                ) : (
                  <FormattedMessage id="CARE_PROVIDER.PAGE.HEADER.PROFILE.NO_VIDEO" />
                )}
              </S.HasVideoSpan>
              {!!specialistInfo?.socialLink && (
                <S.SocialContainer
                  data-testid="socialMedia"
                  onClick={handleSocialNavigation}
                >
                  <Instagram />
                  <S.SocialUsername>
                    {specialistInfo?.socialUsername}
                  </S.SocialUsername>
                </S.SocialContainer>
              )}
            </S.VisualInfoContainer>

            <S.InputsContainer fluid>
              <S.ContainerRows>
                <S.RowsInfo>
                  <Col xxs={6}>
                    <TextField
                      fullWidth
                      data-testid="fullNameInput"
                      label={intl.formatMessage({
                        id: 'CARE_PROVIDER.PAGE.PERSONAL.FULL_NAME.LABEL',
                      })}
                      value={specialistInfo?.fullName || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Col>
                  <Col xxs={6}>
                    <TextField
                      fullWidth
                      data-testid="emailInput"
                      label={intl.formatMessage({
                        id: 'CARE_PROVIDER.PAGE.PERSONAL.EMAIL.LABEL',
                      })}
                      value={specialistInfo?.email || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Col>
                </S.RowsInfo>
                <S.RowsInfo>
                  <Col xxs={3}>
                    <DateField
                      data-testid="birthdayInput"
                      label={intl.formatMessage({ id: 'ENDUSER.INFO.DATE' })}
                      value={new Date(specialistInfo?.birthday || '')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputProps: {
                            readOnly: true,
                          },
                        },
                      }}
                    />
                  </Col>
                  <Col xxs={3}>
                    <TextField
                      fullWidth
                      data-testid="genderInput"
                      label={intl.formatMessage({
                        id: 'CARE_PROVIDER.PAGE.PERSONAL.GENDER.LABEL',
                      })}
                      value={intl.formatMessage({
                        id: `PARTNERSINFORMATION.PERSONALINFOSTAB1.DROPDOWNGENDER${specialistInfo?.gender?.toUpperCase()}`,
                      })}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Col>
                  <Col xxs={3}>
                    <TextField
                      fullWidth
                      data-testid="locationInput"
                      label={intl.formatMessage({
                        id: 'CARE_PROVIDER.PAGE.PERSONAL.LOCATION.LABEL',
                      })}
                      value={specialistInfo?.locale || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Col>
                  <Col xxs={3}>
                    <S.PhoneInputWrapper>
                      <TextField
                        fullWidth
                        data-testid="phoneNumberInput"
                        label={intl.formatMessage({
                          id: 'CARE_PROVIDER.PAGE.PERSONAL.PHONE_NUMBER.LABEL',
                        })}
                        value={specialistInfo?.phoneNumber || ''}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                      {newLoginFlowEnabled && (
                        <S.UpdatePhoneNumberButton
                          icon={S.StyledEdit}
                          onClick={handlePhoneNumberEdit}
                        >
                          {specialistInfo?.phoneNumber
                            ? intl.formatMessage({
                                id: 'TWOFA.BUTTON.EDITMOBILE',
                              })
                            : intl.formatMessage({
                                id: 'TWOFA.BUTTON.ADDMOBILE',
                              })}
                        </S.UpdatePhoneNumberButton>
                      )}
                    </S.PhoneInputWrapper>
                  </Col>
                </S.RowsInfo>
              </S.ContainerRows>
            </S.InputsContainer>
          </S.PersonalInfoContainer>
        </Card.Content>
      </S.StyledCard>
    );
  };

  const ProfessionalInfo = () => {
    if (!hasAccess('profile.professional')) return <></>;
    return (
      <S.StyledCard>
        <S.CardHeader>
          <Text.H5 data-testid="professionalCardTitle">
            <FormattedMessage id="CARE_PROVIDER.PAGE.PROFESSIONAL.CARD.TITLE" />
          </Text.H5>
        </S.CardHeader>

        <Card.Content>
          <S.InputsContainer fluid>
            <S.ContainerRows>
              <S.RowsInfo>
                <Col xxs={4}>
                  <S.InputInfo
                    full
                    disabled
                    cleanable={false}
                    data-testid="occupationInput"
                    label={intl.formatMessage({
                      id: 'CARE_PROVIDER.PAGE.PROFESSIONAL.OCCUPATION.LABEL',
                    })}
                    value={
                      specialistProfessionalInfo?.type
                        ? intl.formatMessage({
                            id: `PROFESSIONAL.${specialistProfessionalInfo?.type}`,
                          })
                        : ''
                    }
                  />
                </Col>
                <Col xxs={4}>
                  <S.InputInfo
                    full
                    disabled
                    cleanable={false}
                    data-testid="professionalNumberInput"
                    label={intl.formatMessage({
                      id: 'CARE_PROVIDER.PAGE.PROFESSIONAL.PROFESSIONAL_NUMBER.LABEL',
                    })}
                    value={specialistProfessionalInfo?.professionalNumber || ''}
                  />
                </Col>
                <Col xxs={4}>
                  <S.InputInfo
                    full
                    disabled
                    cleanable={false}
                    data-testid="epsiInput"
                    label={intl.formatMessage({
                      id: 'CARE_PROVIDER.PAGE.PROFESSIONAL.EPSI.LABEL',
                    })}
                    value={specialistProfessionalInfo?.epsiNumber || ''}
                  />
                </Col>
              </S.RowsInfo>
              <S.RowsInfo>
                <Col xxs={12}>
                  <S.TextAreaInfo
                    full
                    disabled
                    cleanable={false}
                    data-testid="aboutInput"
                    label={intl.formatMessage({
                      id: 'CARE_PROVIDER.PAGE.PROFESSIONAL.ABOUT.LABEL',
                    })}
                    value={specialistProfessionalInfo?.aboutYou || ''}
                  />
                </Col>
              </S.RowsInfo>
            </S.ContainerRows>
          </S.InputsContainer>
          <S.ExperienceSection>
            <S.ExperienceSectionTitle data-testid="experienceTitle">
              <FormattedMessage id="CARE_PROVIDER.PAGE.PROFESSIONAL.EXPERIENCE.TITLE" />
            </S.ExperienceSectionTitle>
            <S.ExperienceContainer>
              {specialistProfessionalInfo?.specialities.map(
                (speciality, idx) => (
                  <S.StyledTag
                    data-testid={`speciality-${idx}`}
                    key={speciality.titleKey}
                  >
                    {intl.formatMessage({
                      id: `PARTNERSINFORMATION.PROFESSIONALINFOSTAB2.DROPDOWNSPECIALTIES${speciality.titleKey}`,
                    })}
                  </S.StyledTag>
                ),
              )}
            </S.ExperienceContainer>
          </S.ExperienceSection>
        </Card.Content>
      </S.StyledCard>
    );
  };

  const AcademicInfo = () => {
    if (!hasAccess('profile.academic')) return <></>;
    return (
      <S.StyledCard>
        <S.CardHeader>
          <Text.H5 data-testid="educationCardTitle">
            <FormattedMessage id="CARE_PROVIDER.PAGE.EDUCATION.CARD.TITLE" />
          </Text.H5>
        </S.CardHeader>

        <Card.Content>
          <S.TextAreaInfo
            full
            disabled
            cleanable={false}
            data-testid="educationInput"
            label={intl.formatMessage({
              id: 'CARE_PROVIDER.PAGE.PROFESSIONAL.EDUCATION.LABEL',
            })}
            value={specialistProfessionalInfo?.education || ''}
          />
        </Card.Content>
      </S.StyledCard>
    );
  };

  useOnInit(() => {
    if (!specialistProfessionalInfo) {
      loadSpecialistProfessionalInfo();
    }
  });

  return (
    <>
      <Loading isLoading={getSpecialistProfessionalInfoRequest.isLoading()} />
      <S.CareProviderInfoContainer>
        {newLoginFlowEnabled && !specialistInfo?.phoneNumber && (
          <UpdatePhoneNumberBanner />
        )}
        <S.HeaderInfoContainer>
          <S.HeaderLabel data-testid="pageTitle">
            <FormattedMessage id="CARE_PROVIDER.PAGE.HEADER.TITLE" />
          </S.HeaderLabel>
          <S.ProfileButton
            type="button"
            onClick={handlePublishedProfileModal}
            data-testid="publishedProfileButton"
          >
            <FormattedMessage id="CARE_PROVIDER.PAGE.HEADER.PROFILE.BUTTON" />
          </S.ProfileButton>
        </S.HeaderInfoContainer>
        <S.Divisor />

        <S.ContentContainer>
          <CareProviderAtualizationInfo />

          <PersonalInfo />

          <ProfessionalInfo />

          <AcademicInfo />
        </S.ContentContainer>
      </S.CareProviderInfoContainer>
    </>
  );
};

export default withAuth(CareProviderInfo);
